import { HistoryEntry, WaterLog, UserGoals, Language, WeightLog } from '../types';
import { calculateWaterLogNutrients } from '../data/drinkBrandsDatabase';

export interface WeightTrendPoint {
  dateStr: string;
  name: string;
  fullDate: string;
  weight: number | null;
  displayWeight: number;
  trend: number;
  note?: string;
}

export interface WeightTrendSummary {
  data: WeightTrendPoint[];
  currentWeight: number;
  startingWeight: number;
  changeKg: number;
  trendSlope: number;
  weeklyRateKg: number;
  averageWeight: number;
  minWeight: number;
  maxWeight: number;
  goalWeight?: number;
  isLosing: boolean;
  isGaining: boolean;
  isStable: boolean;
  totalLogsCount: number;
}

export interface ChartCacheOutput {
  chartData: any[];
  streakHistoryData: any[];
  streak30DayData: any[];
  mealTimeData: any[];
  mealTimingWindowsData: any[];
  trendData: { data: any[]; slope: number; isImproving: boolean; average: number };
  macroTrendData: any[];
  weightTrendData: WeightTrendSummary;
}

const MEMORY_CACHE: Record<string, { timestamp: number; data: ChartCacheOutput }> = {};
const LOCAL_STORAGE_KEY_PREFIX = 'progress_charts_cache_v3_';

function getLocalDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getNutrientStars(val: number, target: number, type: 'limit' | 'macro' | 'target'): number {
  if (val <= 0) return 0;
  if (type === 'limit') {
    return val <= target * 1.25 ? 1 : 0;
  }
  if (type === 'macro') {
    return (val >= target && val <= target * 1.25) ? 1 : 0;
  }
  // Target nutrients (like calories): 5% grace margin (>= 95% of goal) counts as met
  return val >= target * 0.95 ? 1 : 0;
}

export function clearProgressChartsCache(): void {
  for (const key in MEMORY_CACHE) {
    delete MEMORY_CACHE[key];
  }
  try {
    if (typeof localStorage !== 'undefined') {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith(LOCAL_STORAGE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      }
    }
  } catch (e) {
    // Ignore cache clear errors
  }
}

export function computeProgressChartsData(
  history: HistoryEntry[],
  waterLogs: WaterLog[],
  goals: UserGoals,
  progressTab: 'daily' | 'weekly' | 'monthly' | string,
  lang: Language,
  trendMicronutrient: string,
  timeFormat: '12h' | '24h' = '24h',
  weightLogs: WeightLog[] = [],
  goalWeight?: number
): ChartCacheOutput {
  // Generate a deterministic cache key including history total sum & timestamps
  const historySum = history.reduce((sum, h) => sum + (h.total_nutrition?.calories_kcal || 0) + (h.timestamp || 0), 0);
  const historyHash = history.length > 0 ? `${history.length}_${history[0]?.id || ''}_${history[history.length - 1]?.id || ''}_${historySum}` : '0';
  const waterSum = waterLogs.reduce((sum, w) => sum + (w.amount_ml || 0) + (w.timestamp || 0), 0);
  const waterHash = waterLogs.length > 0 ? `${waterLogs.length}_${waterLogs[0]?.id || ''}_${waterSum}` : '0';
  const goalsHash = `${goals.calories}_${goals.protein}_${goals.carbs}_${goals.fat}_${goals.water_ml}_${goals.fiber_g}_${goals.sugar_g}_${goals.sodium_mg}`;
  const weightSum = weightLogs.reduce((sum, w) => sum + (w.weight_kg || 0) + (w.timestamp || 0), 0);
  const weightHash = weightLogs.length > 0 ? `${weightLogs.length}_${weightLogs[0]?.id || ''}_${weightSum}` : '0';
  
  const cacheKey = `${LOCAL_STORAGE_KEY_PREFIX}${progressTab}_${lang}_${trendMicronutrient}_${timeFormat}_${historyHash}_${waterHash}_${goalsHash}_${weightHash}_${goalWeight || 0}`;

  // Check in-memory cache
  if (MEMORY_CACHE[cacheKey]) {
    return MEMORY_CACHE[cacheKey].data;
  }

  // Check persistent localStorage cache
  try {
    const rawStored = localStorage.getItem(cacheKey);
    if (rawStored) {
      const parsed = JSON.parse(rawStored);
      if (parsed && parsed.chartData) {
        MEMORY_CACHE[cacheKey] = { timestamp: Date.now(), data: parsed };
        return parsed;
      }
    }
  } catch (e) {
    // Ignore cache read errors
  }

  // -------------------------------------------------------------
  // HIGH-PERFORMANCE PRE-INDEXING (O(N) single-pass dictionary)
  // -------------------------------------------------------------
  const dateMap = new Map<string, { meals: HistoryEntry[]; water: WaterLog[] }>();

  for (let i = 0; i < history.length; i++) {
    const entry = history[i];
    if (!entry.timestamp) continue;
    const dKey = getLocalDateKey(new Date(entry.timestamp));
    let group = dateMap.get(dKey);
    if (!group) {
      group = { meals: [], water: [] };
      dateMap.set(dKey, group);
    }
    group.meals.push(entry);
  }

  for (let i = 0; i < waterLogs.length; i++) {
    const log = waterLogs[i];
    if (!log.timestamp) continue;
    const dKey = getLocalDateKey(new Date(log.timestamp));
    let group = dateMap.get(dKey);
    if (!group) {
      group = { meals: [], water: [] };
      dateMap.set(dKey, group);
    }
    group.water.push(log);
  }

  const getEntriesForDate = (date: Date) => {
    const dKey = getLocalDateKey(date);
    return dateMap.get(dKey) || { meals: [], water: [] };
  };

  const getLiquidNutrients = (wLogs: WaterLog[]) => {
    let cal = 0;
    let caff = 0;
    let sugar = 0;
    for (let i = 0; i < wLogs.length; i++) {
      const w = wLogs[i];
      if (w.source_meal_id) continue;
      const nutrients = calculateWaterLogNutrients(w);
      cal += nutrients.cal;
      caff += nutrients.caff;
      sugar += nutrients.sugar;
    }
    return { cal, caff, sugar };
  };

  const checkDayCompletedOnDate = (date: Date) => {
    const { meals, water } = getEntriesForDate(date);
    const liquid = getLiquidNutrients(water);
    let dayCals = liquid.cal;
    for (let i = 0; i < meals.length; i++) {
      dayCals += (meals[i].total_nutrition?.calories_kcal || 0);
    }
    return goals.calories > 0 && Math.round(dayCals) >= Math.floor(goals.calories * 0.95);
  };

  // 1. STREAK HISTORY DATA (7 Days)
  const streakHistoryData = [];
  const tempNow = new Date();
  for (let i = 6; i >= 0; i--) {
    const targetDate = new Date(tempNow);
    targetDate.setDate(targetDate.getDate() - i);
    let runningStreak = 0;
    const dateToCheck = new Date(targetDate);
    while (checkDayCompletedOnDate(dateToCheck)) {
      runningStreak++;
      dateToCheck.setDate(dateToCheck.getDate() - 1);
    }
    const label = targetDate.toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-US', { weekday: 'short' });
    const fullDateStr = targetDate.toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-US', { month: 'short', day: 'numeric' });
    streakHistoryData.push({
      name: label,
      fullDate: fullDateStr,
      streak: runningStreak,
      completed: checkDayCompletedOnDate(targetDate) ? (lang === 'ua' ? 'Пройдено' : 'Completed') : (lang === 'ua' ? 'Не пройдено' : 'Not Completed'),
    });
  }

  // 2. STREAK 30-DAY DATA
  const streak30DayData = [];
  const completedCache = new Map<string, boolean>();
  const checkDayCompletedCached = (date: Date) => {
    const key = getLocalDateKey(date);
    if (completedCache.has(key)) return completedCache.get(key)!;
    const completed = checkDayCompletedOnDate(date);
    completedCache.set(key, completed);
    return completed;
  };

  for (let i = 29; i >= 0; i--) {
    const targetDate = new Date(tempNow);
    targetDate.setDate(targetDate.getDate() - i);
    let runningStreak = 0;
    const dateToCheck = new Date(targetDate);
    while (checkDayCompletedCached(dateToCheck)) {
      runningStreak++;
      dateToCheck.setDate(dateToCheck.getDate() - 1);
    }
    const label = targetDate.toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-US', { month: 'short', day: 'numeric' });
    const fullDateStr = targetDate.toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    streak30DayData.push({
      name: label,
      fullDate: fullDateStr,
      streak: runningStreak,
      completed: checkDayCompletedCached(targetDate) ? (lang === 'ua' ? 'Пройдено' : 'Completed') : (lang === 'ua' ? 'Не пройдено' : 'Not Completed'),
    });
  }

  // 3. MAIN CHART DATA (Daily, Weekly, Monthly)
  const chartData: any[] = [];
  const now = new Date();

  if (progressTab === 'daily') {
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const { meals: dayEntries, water: dayWaterLogs } = getEntriesForDate(d);
      const liquidNutrients = getLiquidNutrients(dayWaterLogs);

      let totalCalories = liquidNutrients.cal;
      let totalProtein = 0, totalCarbs = 0, totalFat = 0, totalFiber = 0, totalSugar = 0, totalSodium = 0, totalCaffeine = liquidNutrients.caff;
      let potassium = 0, cholesterol = 0, vitamin_c = 0, calcium = 0, iron = 0;

      for (let j = 0; j < dayEntries.length; j++) {
        const e = dayEntries[j];
        const nut = e.total_nutrition;
        if (!nut) continue;
        totalCalories += (nut.calories_kcal || 0);
        totalProtein += (nut.protein_g || 0);
        totalCarbs += (nut.carbs_g || 0);
        totalFat += (nut.fat_g || 0);
        totalFiber += (nut.fiber_g || 0);
        totalSugar += (nut.sugar_g || 0);
        totalSodium += (nut.sodium_mg || 0);
        totalCaffeine += (nut.caffeine_mg || 0);
        potassium += (nut.potassium_mg || 0);
        cholesterol += (nut.cholesterol_mg || 0);
        vitamin_c += (nut.vitamin_c_mg || 0);
        calcium += (nut.calcium_mg || 0);
        iron += (nut.iron_mg || 0);
      }

      let totalWater = 0;
      for (let j = 0; j < dayWaterLogs.length; j++) {
        totalWater += dayWaterLogs[j].amount_ml;
      }

      const dayStars =
        getNutrientStars(totalCalories, goals.calories, 'target') +
        getNutrientStars(totalWater, goals.water_ml, 'target') +
        getNutrientStars(totalProtein, goals.protein, 'target') +
        getNutrientStars(totalFiber, goals.fiber_g, 'target') +
        getNutrientStars(totalCarbs, goals.carbs, 'macro') +
        getNutrientStars(totalFat, goals.fat, 'macro') +
        getNutrientStars(totalSugar, goals.sugar_g, 'limit') +
        getNutrientStars(totalSodium, goals.sodium_mg, 'limit') +
        getNutrientStars(totalCaffeine, goals.caffeine_mg || 400, 'limit');

      chartData.push({
        name: d.toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-US', { weekday: 'short' }),
        calories: totalCalories,
        protein: totalProtein,
        carbs: totalCarbs,
        fat: totalFat,
        water: totalWater,
        fiber: totalFiber,
        sugar: totalSugar,
        sodium: totalSodium,
        salt: totalSodium / 1000,
        caffeine: totalCaffeine,
        stars: dayStars,
        potassium,
        cholesterol,
        vitamin_c,
        calcium,
        iron,
      });
    }
  } else if (progressTab === 'weekly') {
    for (let i = 3; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - (i * 7));
      const start = new Date(d);
      start.setDate(start.getDate() - start.getDay());

      let weekCalories = 0, weekProtein = 0, weekCarbs = 0, weekFat = 0, weekWater = 0, weekFiber = 0, weekSugar = 0, weekSodium = 0, weekCaffeine = 0;
      let weekPotassium = 0, weekCholesterol = 0, weekVitaminC = 0, weekCalcium = 0, weekIron = 0;
      let totalStarsInPeriod = 0;

      for (let j = 0; j < 7; j++) {
        const dayDate = new Date(start);
        dayDate.setDate(dayDate.getDate() + j);
        const { meals: dayEntries, water: dayWaterLogs } = getEntriesForDate(dayDate);
        const liquidNutrients = getLiquidNutrients(dayWaterLogs);

        let dCal = liquidNutrients.cal;
        let dProt = 0, dCarb = 0, dFat = 0, dFiber = 0, dSugar = 0, dSodium = 0, dCaff = liquidNutrients.caff;

        for (let k = 0; k < dayEntries.length; k++) {
          const nut = dayEntries[k].total_nutrition;
          if (!nut) continue;
          dCal += (nut.calories_kcal || 0);
          dProt += (nut.protein_g || 0);
          dCarb += (nut.carbs_g || 0);
          dFat += (nut.fat_g || 0);
          dFiber += (nut.fiber_g || 0);
          dSugar += (nut.sugar_g || 0);
          dSodium += (nut.sodium_mg || 0);
          dCaff += (nut.caffeine_mg || 0);
          weekPotassium += (nut.potassium_mg || 0);
          weekCholesterol += (nut.cholesterol_mg || 0);
          weekVitaminC += (nut.vitamin_c_mg || 0);
          weekCalcium += (nut.calcium_mg || 0);
          weekIron += (nut.iron_mg || 0);
        }

        let dWater = 0;
        for (let k = 0; k < dayWaterLogs.length; k++) {
          dWater += dayWaterLogs[k].amount_ml;
        }

        weekCalories += dCal;
        weekProtein += dProt;
        weekCarbs += dCarb;
        weekFat += dFat;
        weekWater += dWater;
        weekFiber += dFiber;
        weekSugar += dSugar;
        weekSodium += dSodium;
        weekCaffeine += dCaff;

        totalStarsInPeriod +=
          getNutrientStars(dCal, goals.calories, 'target') +
          getNutrientStars(dWater, goals.water_ml, 'target') +
          getNutrientStars(dProt, goals.protein, 'target') +
          getNutrientStars(dFiber, goals.fiber_g, 'target') +
          getNutrientStars(dCarb, goals.carbs, 'macro') +
          getNutrientStars(dFat, goals.fat, 'macro') +
          getNutrientStars(dSugar, goals.sugar_g, 'limit') +
          getNutrientStars(dSodium, goals.sodium_mg, 'limit') +
          getNutrientStars(dCaff, goals.caffeine_mg || 400, 'limit');
      }

      chartData.push({
        name: `W${4 - i}`,
        calories: weekCalories / 7,
        protein: weekProtein / 7,
        carbs: weekCarbs / 7,
        fat: weekFat / 7,
        water: weekWater / 7,
        fiber: weekFiber / 7,
        sugar: weekSugar / 7,
        sodium: weekSodium / 7,
        salt: (weekSodium / 7) / 1000,
        caffeine: weekCaffeine / 7,
        stars: totalStarsInPeriod / 7,
        potassium: weekPotassium / 7,
        cholesterol: weekCholesterol / 7,
        vitamin_c: weekVitaminC / 7,
        calcium: weekCalcium / 7,
        iron: weekIron / 7,
      });
    }
  } else {
    // Monthly (6 months)
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now);
      d.setMonth(d.getMonth() - i);
      const month = d.getMonth();
      const year = d.getFullYear();
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      let monthCalories = 0, monthProtein = 0, monthCarbs = 0, monthFat = 0, monthWater = 0, monthFiber = 0, monthSugar = 0, monthSodium = 0, monthCaffeine = 0;
      let monthPotassium = 0, monthCholesterol = 0, monthVitaminC = 0, monthCalcium = 0, monthIron = 0;
      let totalStarsInPeriod = 0;

      for (let j = 1; j <= daysInMonth; j++) {
        const dayDate = new Date(year, month, j);
        const { meals: dayEntries, water: dayWaterLogs } = getEntriesForDate(dayDate);
        const liquidNutrients = getLiquidNutrients(dayWaterLogs);

        let dCal = liquidNutrients.cal;
        let dProt = 0, dCarb = 0, dFat = 0, dFiber = 0, dSugar = 0, dSodium = 0, dCaff = liquidNutrients.caff;

        for (let k = 0; k < dayEntries.length; k++) {
          const nut = dayEntries[k].total_nutrition;
          if (!nut) continue;
          dCal += (nut.calories_kcal || 0);
          dProt += (nut.protein_g || 0);
          dCarb += (nut.carbs_g || 0);
          dFat += (nut.fat_g || 0);
          dFiber += (nut.fiber_g || 0);
          dSugar += (nut.sugar_g || 0);
          dSodium += (nut.sodium_mg || 0);
          dCaff += (nut.caffeine_mg || 0);
          monthPotassium += (nut.potassium_mg || 0);
          monthCholesterol += (nut.cholesterol_mg || 0);
          monthVitaminC += (nut.vitamin_c_mg || 0);
          monthCalcium += (nut.calcium_mg || 0);
          monthIron += (nut.iron_mg || 0);
        }

        let dWater = 0;
        for (let k = 0; k < dayWaterLogs.length; k++) {
          dWater += dayWaterLogs[k].amount_ml;
        }

        monthCalories += dCal;
        monthProtein += dProt;
        monthCarbs += dCarb;
        monthFat += dFat;
        monthWater += dWater;
        monthFiber += dFiber;
        monthSugar += dSugar;
        monthSodium += dSodium;
        monthCaffeine += dCaff;

        totalStarsInPeriod +=
          getNutrientStars(dCal, goals.calories, 'target') +
          getNutrientStars(dWater, goals.water_ml, 'target') +
          getNutrientStars(dProt, goals.protein, 'target') +
          getNutrientStars(dFiber, goals.fiber_g, 'target') +
          getNutrientStars(dCarb, goals.carbs, 'macro') +
          getNutrientStars(dFat, goals.fat, 'macro') +
          getNutrientStars(dSugar, goals.sugar_g, 'limit') +
          getNutrientStars(dSodium, goals.sodium_mg, 'limit') +
          getNutrientStars(dCaff, goals.caffeine_mg || 400, 'limit');
      }

      chartData.push({
        name: d.toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-US', { month: 'short' }),
        calories: monthCalories / 30,
        protein: monthProtein / 30,
        carbs: monthCarbs / 30,
        fat: monthFat / 30,
        water: monthWater / 30,
        fiber: monthFiber / 30,
        sugar: monthSugar / 30,
        sodium: monthSodium / 30,
        salt: (monthSodium / 30) / 1000,
        caffeine: monthCaffeine / 30,
        stars: totalStarsInPeriod / daysInMonth,
        potassium: monthPotassium / 30,
        cholesterol: monthCholesterol / 30,
        vitamin_c: monthVitaminC / 30,
        calcium: monthCalcium / 30,
        iron: monthIron / 30,
      });
    }
  }

  // 4. MEAL TIME DISTRIBUTION (24 Hours)
  let filteredEntries: HistoryEntry[] = [];
  let filteredWater: WaterLog[] = [];
  const limitDate = new Date(now);

  if (progressTab === 'daily') {
    limitDate.setDate(limitDate.getDate() - 7);
    filteredEntries = history.filter(e => new Date(e.timestamp) >= limitDate);
    filteredWater = waterLogs.filter(w => new Date(w.timestamp) >= limitDate);
  } else if (progressTab === 'weekly') {
    limitDate.setDate(limitDate.getDate() - 28);
    filteredEntries = history.filter(e => new Date(e.timestamp) >= limitDate);
    filteredWater = waterLogs.filter(w => new Date(w.timestamp) >= limitDate);
  } else if (progressTab === 'monthly') {
    limitDate.setMonth(limitDate.getMonth() - 6);
    filteredEntries = history.filter(e => new Date(e.timestamp) >= limitDate);
    filteredWater = waterLogs.filter(w => new Date(w.timestamp) >= limitDate);
  } else {
    filteredEntries = history;
    filteredWater = waterLogs;
  }

  const mealTimeDistribution = Array.from({ length: 24 }, (_, hour) => {
    let label = '';
    if (timeFormat === '12h') {
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 === 0 ? 12 : hour % 12;
      label = `${displayHour} ${ampm}`;
    } else {
      label = `${String(hour).padStart(2, '0')}:00`;
    }
    return { hour, name: label, calories: 0, mealCount: 0 };
  });

  for (let i = 0; i < filteredEntries.length; i++) {
    const entry = filteredEntries[i];
    const hour = new Date(entry.timestamp).getHours();
    if (hour >= 0 && hour < 24) {
      mealTimeDistribution[hour].calories += (entry.total_nutrition?.calories_kcal || 0);
      mealTimeDistribution[hour].mealCount += 1;
    }
  }

  for (let i = 0; i < filteredWater.length; i++) {
    const w = filteredWater[i];
    if (w.source_meal_id) continue;
    const hour = new Date(w.timestamp).getHours();
    const { cal } = calculateWaterLogNutrients(w);
    if (hour >= 0 && hour < 24) {
      mealTimeDistribution[hour].calories += cal;
    }
  }

  const mealTimeData = mealTimeDistribution.map(d => ({
    ...d,
    calories: Math.round(d.calories),
  }));

  // 5. MEAL TIMING WINDOWS (6 4-Hour Windows)
  const windows = [
    { name: lang === 'ua' ? '00:00 - 04:00' : '12 AM - 4 AM', count: 0, label: lang === 'ua' ? 'Ніч' : 'Night' },
    { name: lang === 'ua' ? '04:00 - 08:00' : '4 AM - 8 AM', count: 0, label: lang === 'ua' ? 'Ранок' : 'Morning' },
    { name: lang === 'ua' ? '08:00 - 12:00' : '8 AM - 12 PM', count: 0, label: lang === 'ua' ? 'День' : 'Midday' },
    { name: lang === 'ua' ? '12:00 - 16:00' : '12 PM - 4 PM', count: 0, label: lang === 'ua' ? 'Обід' : 'Afternoon' },
    { name: lang === 'ua' ? '16:00 - 20:00' : '4 PM - 8 PM', count: 0, label: lang === 'ua' ? 'Вечір' : 'Evening' },
    { name: lang === 'ua' ? '20:00 - 00:00' : '8 PM - 12 AM', count: 0, label: lang === 'ua' ? 'Пізній вечір' : 'Late Night' },
  ];

  for (let i = 0; i < filteredEntries.length; i++) {
    const hour = new Date(filteredEntries[i].timestamp).getHours();
    const windowIdx = Math.floor(hour / 4) % 6;
    windows[windowIdx].count += 1;
  }
  const mealTimingWindowsData = windows;

  // 6. 30-DAY MICRONUTRIENT TREND
  const data30DaysMicro = [];
  const tNowMicro = new Date();
  tNowMicro.setHours(0, 0, 0, 0);

  for (let i = 29; i >= 0; i--) {
    const targetDate = new Date(tNowMicro);
    targetDate.setDate(targetDate.getDate() - i);
    const { meals: dayEntries } = getEntriesForDate(targetDate);

    let totalIntake = 0;
    for (let j = 0; j < dayEntries.length; j++) {
      const val = Number(dayEntries[j].total_nutrition?.[trendMicronutrient as keyof typeof dayEntries[0]['total_nutrition']]);
      if (!isNaN(val)) totalIntake += val;
    }

    const label = targetDate.toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-US', { month: 'short', day: 'numeric' });
    data30DaysMicro.push({
      dateStr: getLocalDateKey(targetDate),
      name: label,
      intake: Number(totalIntake.toFixed(1)),
    });
  }

  const n = data30DaysMicro.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
  for (let i = 0; i < n; i++) {
    const x = i;
    const y = data30DaysMicro[i].intake;
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
  }
  const meanX = sumX / n;
  const meanY = sumY / n;

  let num = 0, den = 0;
  for (let i = 0; i < n; i++) {
    const x = i;
    const y = data30DaysMicro[i].intake;
    num += (x - meanX) * (y - meanY);
    den += (x - meanX) * (x - meanX);
  }
  const slope = den === 0 ? 0 : num / den;
  const intercept = meanY - slope * meanX;

  const finalMicroTrend = data30DaysMicro.map((item, index) => {
    const trendValue = slope * index + intercept;
    return {
      ...item,
      trend: Number(Math.max(0, trendValue).toFixed(1)),
    };
  });

  const trendData = {
    data: finalMicroTrend,
    slope,
    isImproving: slope > 0,
    average: Number(meanY.toFixed(1)),
  };

  // 7. 30-DAY MACRONUTRIENT TREND
  const macroTrendData = [];
  const tNowMacro = new Date();
  tNowMacro.setHours(0, 0, 0, 0);

  for (let i = 29; i >= 0; i--) {
    const targetDate = new Date(tNowMacro);
    targetDate.setDate(targetDate.getDate() - i);
    const { meals: dayEntries } = getEntriesForDate(targetDate);

    let p = 0, c = 0, f = 0, fib = 0;
    for (let j = 0; j < dayEntries.length; j++) {
      const nut = dayEntries[j].total_nutrition;
      if (!nut) continue;
      if (!isNaN(Number(nut.protein_g))) p += Number(nut.protein_g);
      if (!isNaN(Number(nut.carbs_g))) c += Number(nut.carbs_g);
      if (!isNaN(Number(nut.fat_g))) f += Number(nut.fat_g);
      if (!isNaN(Number(nut.fiber_g))) fib += Number(nut.fiber_g);
    }

    const label = targetDate.toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-US', { month: 'short', day: 'numeric' });
    macroTrendData.push({
      dateStr: getLocalDateKey(targetDate),
      name: label,
      protein: Number(p.toFixed(1)),
      carbs: Number(c.toFixed(1)),
      fat: Number(f.toFixed(1)),
      fiber: Number(fib.toFixed(1)),
    });
  }

  // 8. BODY WEIGHT HISTORY & TREND LINE CALCULATION
  const weightMap = new Map<string, WeightLog>();
  const sortedLogs = [...weightLogs].sort((a, b) => a.timestamp - b.timestamp);
  for (let i = 0; i < sortedLogs.length; i++) {
    const log = sortedLogs[i];
    if (log.timestamp && log.weight_kg > 0) {
      weightMap.set(getLocalDateKey(new Date(log.timestamp)), log);
    }
  }

  const daysToTrack = progressTab === 'monthly' ? 60 : progressTab === 'weekly' ? 28 : 14;
  const weightPoints: WeightTrendPoint[] = [];
  const tNowWeight = new Date();
  tNowWeight.setHours(0, 0, 0, 0);

  // Find baseline weight if available
  let latestKnownWeight = sortedLogs.length > 0 ? sortedLogs[sortedLogs.length - 1].weight_kg : 70;
  let firstKnownWeight = sortedLogs.length > 0 ? sortedLogs[0].weight_kg : latestKnownWeight;

  // Track values for regression
  const measuredPoints: { index: number; weight: number }[] = [];

  for (let i = daysToTrack - 1; i >= 0; i--) {
    const targetDate = new Date(tNowWeight);
    targetDate.setDate(targetDate.getDate() - i);
    const dKey = getLocalDateKey(targetDate);
    const log = weightMap.get(dKey);
    const idx = daysToTrack - 1 - i;

    const label = targetDate.toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
    const fullDateStr = targetDate.toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });

    if (log) {
      latestKnownWeight = log.weight_kg;
      measuredPoints.push({ index: idx, weight: log.weight_kg });
      weightPoints.push({
        dateStr: dKey,
        name: label,
        fullDate: fullDateStr,
        weight: Number(log.weight_kg.toFixed(1)),
        displayWeight: Number(log.weight_kg.toFixed(1)),
        trend: 0,
        note: log.note
      });
    } else {
      weightPoints.push({
        dateStr: dKey,
        name: label,
        fullDate: fullDateStr,
        weight: null,
        displayWeight: Number(latestKnownWeight.toFixed(1)),
        trend: 0
      });
    }
  }

  // Calculate Linear Regression Trend Line
  let weightSlope = 0;
  let weightIntercept = latestKnownWeight;

  if (measuredPoints.length >= 2) {
    const mN = measuredPoints.length;
    let mSumX = 0, mSumY = 0, mSumXY = 0, mSumXX = 0;
    for (let i = 0; i < mN; i++) {
      const p = measuredPoints[i];
      mSumX += p.index;
      mSumY += p.weight;
      mSumXY += p.index * p.weight;
      mSumXX += p.index * p.index;
    }
    const mMeanX = mSumX / mN;
    const mMeanY = mSumY / mN;
    let mNum = 0, mDen = 0;
    for (let i = 0; i < mN; i++) {
      const p = measuredPoints[i];
      mNum += (p.index - mMeanX) * (p.weight - mMeanY);
      mDen += (p.index - mMeanX) * (p.index - mMeanX);
    }
    weightSlope = mDen === 0 ? 0 : mNum / mDen;
    weightIntercept = mMeanY - weightSlope * mMeanX;
  } else if (sortedLogs.length >= 2) {
    // Estimate from sorted logs if span exceeds current chart window
    const first = sortedLogs[0];
    const last = sortedLogs[sortedLogs.length - 1];
    const daysDiff = Math.max(1, (last.timestamp - first.timestamp) / 86400000);
    weightSlope = (last.weight_kg - first.weight_kg) / daysDiff;
    weightIntercept = last.weight_kg - weightSlope * (daysToTrack - 1);
  }

  // Apply trend values to each point
  for (let i = 0; i < weightPoints.length; i++) {
    const rawTrend = weightSlope * i + weightIntercept;
    weightPoints[i].trend = Number(Math.max(20, rawTrend).toFixed(1));
  }

  const allWeights = sortedLogs.map(l => l.weight_kg).filter(w => w > 0);
  const currentW = sortedLogs.length > 0 ? sortedLogs[sortedLogs.length - 1].weight_kg : latestKnownWeight;
  const startingW = sortedLogs.length > 0 ? sortedLogs[0].weight_kg : currentW;
  const minW = allWeights.length > 0 ? Math.min(...allWeights) : currentW;
  const maxW = allWeights.length > 0 ? Math.max(...allWeights) : currentW;
  const avgW = allWeights.length > 0 ? allWeights.reduce((a, b) => a + b, 0) / allWeights.length : currentW;

  const weightTrendData: WeightTrendSummary = {
    data: weightPoints,
    currentWeight: Number(currentW.toFixed(1)),
    startingWeight: Number(startingW.toFixed(1)),
    changeKg: Number((currentW - startingW).toFixed(1)),
    trendSlope: Number(weightSlope.toFixed(3)),
    weeklyRateKg: Number((weightSlope * 7).toFixed(2)),
    averageWeight: Number(avgW.toFixed(1)),
    minWeight: Number(minW.toFixed(1)),
    maxWeight: Number(maxW.toFixed(1)),
    goalWeight: goalWeight ? Number(goalWeight.toFixed(1)) : undefined,
    isLosing: weightSlope < -0.015,
    isGaining: weightSlope > 0.015,
    isStable: Math.abs(weightSlope) <= 0.015,
    totalLogsCount: sortedLogs.length
  };

  const result: ChartCacheOutput = {
    chartData,
    streakHistoryData,
    streak30DayData,
    mealTimeData,
    mealTimingWindowsData,
    trendData,
    macroTrendData,
    weightTrendData,
  };

  // Cache in-memory and persistent localStorage
  MEMORY_CACHE[cacheKey] = { timestamp: Date.now(), data: result };
  try {
    localStorage.setItem(cacheKey, JSON.stringify(result));
  } catch (e) {
    // Ignore quota/write errors
  }

  return result;
}
